import { useMutation } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { Site } from "../../api/Api";
import { apiClient } from "../../api/ApiClient";
import FormLabel from "../login/formLabel/FormLabel";
import Button from "../mantine/button/Button";
import NotificationBlock from "../notificationBlock/NotificationBlock";
import Input from "../ui/input/Input";
import Modal from "../ui/modal/Modal";
import Spinner from "../ui/spinner/Spinner";
import styles from "./SiteSettings.module.scss";

type AuthorizationType =
  | "recrawl"
  | "crawl"
  | "destroy"
  | "update"
  | "widget_toggle"
  | "site_speed_apply";

interface Authentication {
  collection: string[];
  member: AuthorizationType[][];
}

export interface SiteWithAuth {
  site: Site;
  authorizations: Authentication;
}

type GeneralProps = {
  siteData: SiteWithAuth | undefined;
  isLoading: boolean;
};

const General = ({ siteData, isLoading }: GeneralProps) => {
  const navigate = useNavigate();

  const { mutate: deleteSite } = useMutation({
    mutationFn: (id: number) => apiClient.sites.sitesDelete(id),

    onError: (error) => {
      //@ts-expect-error be responce
      const errorMessage = error?.error?.error?.message || "Error";
      enqueueSnackbar(errorMessage, {
        variant: "error",
        persist: true,
      });
    },
    onSuccess: () => {
      enqueueSnackbar("Site was successfully deleted", {
        variant: "success",
      });
      navigate("/");
    },
  });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const { register, watch } = useForm({
    mode: "onChange",
    defaultValues: {
      confirmDomain: "",
    },
  });
  const confirmDomain = watch("confirmDomain");
  const isConfirmed = confirmDomain === siteData?.site?.fqdn;
  const openDeleteModal = () => setIsDeleteModalOpen(true);
  const closeDeleteModal = () => setIsDeleteModalOpen(false);

  const handleDeleteWebsite = () => {
    if (isConfirmed) {
      deleteSite(siteData?.site?.id);
      closeDeleteModal();
    }
  };
  const canDeleteSite = siteData?.authorizations?.member
    ?.flat()
    .includes("destroy");
  if (isLoading) return <Spinner />;

  return (
    <>
      <p className={styles.settingsSectionText}>
        Allows you to view general site information about your website and
        manage its settings. Empowers users to oversee various aspects of their
        website's configuration
      </p>
      <div className={styles.settingsForm}>
        <div className={styles.settingsForm}>
          <FormLabel label="Website URL" />
          <Input
            disabled
            id="website-url"
            defaultValue={siteData?.site?.fqdn}
            aria-label="Website URL"
          />
        </div>
      </div>

      <section>
        <FormLabel label="Danger zone" />
        <p className={styles.settingsSectionText}>
          You can delete website from your team entirely but all the information
          and progress will be lost
        </p>
        {canDeleteSite && (
          <div className={styles.settingsDelete}>
            <Button
              variant="danger-outline"
              onClick={openDeleteModal}
              aria-label="Delete website"
            >
              Delete Website
            </Button>
          </div>
        )}
      </section>

      <Modal
        isOpen={isDeleteModalOpen}
        closeModal={closeDeleteModal}
        modalLabel={
          // @ts-expect-error TS2339 non-existing className
          <h3 className={styles.modalTitle}>Confirm Website Deletion</h3>
        }
        buttonLabel="Delete"
        action={handleDeleteWebsite}
        actionButtonColor="danger"
        isDisabled={!isConfirmed}
      >
        <>
          <p>
            Seems like you are going to delete your{" "}
            <strong>{siteData?.site?.fqdn}</strong> from your team. These will
            lead to the permanent loss of the website information{" "}
          </p>
          <p>
            <NotificationBlock
              type="warning"
              message="Pay attention! Action cannot be reversed. If you are using site speed optimization tool don`t forget to transfer your website domain to different hosting, or it might disrupt the work of your website"
            />
          </p>
          <p>
            <FormLabel label="Confirm Deletion" />
            <Input
              id="delete-confirmation"
              {...register("confirmDomain", {
                validate: (value) => value === siteData?.site?.fqdn,
              })}
              placeholder={`Type ${siteData?.site?.fqdn} to confirm`}
            />
          </p>
        </>
      </Modal>
    </>
  );
};

export default General;
